import * as React from 'react';
import { useState } from 'react';
import {FormattedMessage} from 'react-intl';

import {SITE, HeadlineLevel} from 'features/ui/enums';
import {PageModel} from "features/template/types";
import {getCurrentSite} from 'features/services/utils/env-helper';
import {useBreadcrumbs} from "features/template/ViewModelProvider";
import {CorporateAboutStartPageViewModel} from 'features/template/viewModels/CorporateAboutStartPageViewModel';
import { useDeviceStore, selectIsMobile, selectIsTablet } from 'features/ui/store/useDeviceStore';

import './style.scss';

import {P} from 'components/atoms/Text/P';
import {MainGrid} from 'components/atoms/Layout/MainGrid';
import {Container} from 'components/atoms/Layout/Container';
import { ArticleTeaser } from '@molecules/ContentLinkCard/ArticleTeaser';
import {ContentArea} from 'components/organisms/GeneralComponents/ContentArea';
import {Breadcrumbs} from 'components/molecules/NavigationComponents/Breadcrumbs';
import {AboutUsQuickFacts} from '@molecules/ResidencePageComponents/QuickFacts/_aboutUs';
import {getHeaderBlock} from "components/blocks/header-block-helper";
import {CollapsableText} from 'components/molecules/TextShowHide/CollapsableText';
import { Button, ButtonColor, ButtonSize, ButtonState, ButtonType } from '@atoms/LinksAndButtons/Button';
import {StructuredDataBreadcrumbs} from "@molecules/StructuredDataBreadcrumbs";
import {Headline} from '@atoms/Text/Headline';

type Props = PageModel<CorporateAboutStartPageViewModel>;

export const CorporateAboutStartPage: React.FunctionComponent<Props> = ({viewModel}) => {
    const isMobile = useDeviceStore(selectIsMobile);
    const isTablet = useDeviceStore(selectIsTablet);
    const breadcrumbs = useBreadcrumbs();
    const [activeFilter, setActiveFilter] = useState<string>('all');
    const groupLength = 0 + (viewModel.contentAreaJmGroupSweden.length > 0 ? 1 : 0) + (viewModel.contentAreaJmGroupNorway.length > 0 ? 1 : 0) + (viewModel.contentAreaJmGroupFinland.length > 0 ? 1 : 0);

    const getSiteLabel = () => {
        const currentSite = getCurrentSite();

        switch (currentSite) {
            case SITE.SENIORGARDEN:
                return "Seniorgården";
            case SITE.BORATT:
                return "Borätt";
            default:
                return "JM";
        }
    };
    const filterArticles = (filter: string) => () => {
        setActiveFilter(filter);
    };

    return (
        <div className="corporate-about-start-page">
            <StructuredDataBreadcrumbs breadcrumbs={breadcrumbs} />
            <Breadcrumbs
                breadcrumbs={breadcrumbs}
            />

            {viewModel.headerBlock &&
                getHeaderBlock(viewModel.headerBlock)
            }

            <MainGrid className="info-section" fullwidth>
                <div className="text-container">
                    <Headline level={HeadlineLevel.H2}>
                        {viewModel.header}
                    </Headline>

                    {viewModel.preamble &&
                        <P preamble>
                            {viewModel.preamble}
                        </P>
                    }

                    {viewModel.text &&
                        <CollapsableText
                            showMore
                            description={viewModel.text}
                        />
                    }
                </div>

                <div className='quick-facts show-more'>
                    {(viewModel.market || viewModel.revenue || viewModel.employees || viewModel.founded || viewModel.availableBuildingRights) &&
                        <AboutUsQuickFacts viewModel={viewModel} />
                    }
                </div>
            </MainGrid>

            <ContentArea id="" blocks={viewModel.contentAreaTop}/>

            {(viewModel.contentAreaGetToKnowJm && viewModel.contentAreaGetToKnowJm.length > 0) &&
                <div className="jm-group">
                    <Headline level={HeadlineLevel.H2} center>
                        <FormattedMessage id="jm.components.CorporateAboutStartPage.getToKnowJm" values={{company: getSiteLabel()}} />
                    </Headline>
                    <ContentArea
                        blocks={viewModel.contentAreaGetToKnowJm}
                        className="main-grid"
                        foldable
                        foldAt={isTablet ? 4 : isMobile ? 3 : 6 }
                        id="content-area-get-to-know"
                        hideCopyButton
                    />
                </div>
            }

            {/* {(viewModel.contentAreaJmEntreprenadGroupSweden && viewModel.contentAreaJmEntreprenadGroupSweden.length > 0) &&
                <div className="jm-group">
                    <H2 center>
                        <FormattedMessage id="jm.components.CorporateAboutStartPage.getToKnowJm" values={{company: getSiteLabel()}} />
                    </H2>
                    <ContentArea
                        blocks={viewModel.contentAreaJmEntreprenadGroupSweden}
                        className="main-grid"
                        foldable
                        foldAt={isTablet ? 4 : isMobile ? 3 : 6 }
                        id="content-area-get-to-know"
                        hideCopyButton
                    />
                </div>
            } */}

            {(groupLength > 0) &&
                <div className="jm-group">
                    <Headline level={HeadlineLevel.H2} center><FormattedMessage id="jm.components.CorporateAboutStartPage.jmGroup" /></Headline>
                    {groupLength > 1 && <Container>
                        <div className="toggle-buttons toggle-buttons--multi">
                            <Button 
                                buttonState={activeFilter === 'all'
                                    ? ButtonState.ACTIVE
                                    : ButtonState.DEFAULT
                                } 
                                onClick={filterArticles('all')} 
                                buttonColor={ButtonColor.DEFAULT} 
                                buttonSize={ButtonSize.DEFAULT} 
                                buttonType={ButtonType.TOGGLE} 
                            >
                                <FormattedMessage id="jm.components.SustainabilityStartPage.filterAll" />
                            </Button>
                            {viewModel.contentAreaJmGroupSweden.length > 0 && 
                                <Button 
                                    buttonState={activeFilter === 'se'
                                        ? ButtonState.ACTIVE
                                        : ButtonState.DEFAULT
                                    } 
                                    onClick={filterArticles('se')} 
                                    buttonColor={ButtonColor.DEFAULT} 
                                    buttonSize={ButtonSize.DEFAULT} 
                                    buttonType={ButtonType.TOGGLE} 
                                >
                                    <FormattedMessage id="jm.components.CorporateAboutStartPage.sweden" />
                                </Button>
                            }
                            {viewModel.contentAreaJmGroupNorway.length > 0 && 
                                <Button 
                                    buttonState={activeFilter === 'no'
                                        ? ButtonState.ACTIVE
                                        : ButtonState.DEFAULT
                                    } 
                                    onClick={filterArticles('no')} 
                                    buttonColor={ButtonColor.DEFAULT} 
                                    buttonSize={ButtonSize.DEFAULT} 
                                    buttonType={ButtonType.TOGGLE} 
                                >
                                    <FormattedMessage id="jm.components.CorporateAboutStartPage.norway" />
                                </Button>
                            }
                            {viewModel.contentAreaJmGroupFinland.length > 0 && 
                                <Button 
                                    buttonState={activeFilter === 'fi'
                                        ? ButtonState.ACTIVE
                                        : ButtonState.DEFAULT
                                    } 
                                    onClick={filterArticles('fi')} 
                                    buttonColor={ButtonColor.DEFAULT} 
                                    buttonSize={ButtonSize.DEFAULT} 
                                    buttonType={ButtonType.TOGGLE} 
                                >
                                    <FormattedMessage id="jm.components.CorporateAboutStartPage.finland" />
                                </Button>
                            }
                        </div>
                    </Container>}
                    <MainGrid>
                        {viewModel.contentAreaJmGroupSweden.map((item, index) => {
                            if(!item.data.title || !item.data.linkUrl) return;
                            if(activeFilter !== 'all' && activeFilter !== 'se') return;
                            return (
                                <ArticleTeaser
                                    title={item.data.title}
                                    text={item.data.text}
                                    linkUrl={item.data.linkUrl}
                                    image={item.data.image}
                                    className="type-1"
                                    key={'swe-' + index}
                                    filterField='se'
                                />
                            );
                        })}
                        {viewModel.contentAreaJmGroupNorway.map((item, index) => {
                            if(!item.data.title || !item.data.linkUrl) return;
                            if(activeFilter !== 'all' && activeFilter !== 'no') return;
                            return (
                                <ArticleTeaser
                                    title={item.data.title}
                                    text={item.data.text}
                                    linkUrl={item.data.linkUrl}
                                    image={item.data.image}
                                    className="type-1"
                                    key={'no-' + index}
                                    filterField='no'
                                />
                            );
                        })}
                        {viewModel.contentAreaJmGroupFinland.map((item, index) => {
                            if(!item.data.title || !item.data.linkUrl) return;
                            if(activeFilter !== 'all' && activeFilter !== 'fi') return;
                            return (
                                <ArticleTeaser
                                    title={item.data.title}
                                    text={item.data.text}
                                    linkUrl={item.data.linkUrl}
                                    image={item.data.image}
                                    className="type-1"
                                    key={'fi-' + index}
                                    filterField='fi'
                                />
                            );
                        })}
                    </MainGrid>
                </div>
            }

            <ContentArea id="" blocks={viewModel.contentAreaBottom}/>

        </div>
    );
};
